import { Component, EventEmitter, OnDestroy, OnInit, Output,
  ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { DropdownComponent } from '@appkit4/angular-components/dropdown';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { CoaDiagnosticFixService } from './coa-diagnostic-fix.service';

@Component({
    selector: 'gtw-coa-diagnostic-fix',
    templateUrl: './coa-diagnostic-fix.component.html',
    styleUrls: ['./coa-diagnostic-fix.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CoaDiagnosticFixComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  @ViewChild('acctMultiSelect') acctMultiSelect?: DropdownComponent;

  private readonly subs = new Subscription();
  private isInitialized = false;

  /**
   * Capture-phase document click handler used to close the accounts multiselect.
   * The left AppKit nav (`ap-tooltip`) calls `stopPropagation()` in the bubble
   * phase, so AppKit's overlay click-outside handling can miss those
   * clicks and the panel would stay open. A capture-phase listener is not
   * affected by bubble-phase `stopPropagation()`.
   */
  private readonly onDocClickCapture = (event: MouseEvent): void =>
    this.closeMultiSelectIfOutside(event);

  GENERAL_CONFIG: any = {};
  private headerData: any = {};
  private globalParams: any = {};
  private userSettings: any = {};
  filterParams: any = {};

  curentyear = '';
  scenario = '';
  jcd_key = '';

  filteredTaxYearData: any[] = [];
  scenarioDropDown: any[] = [];
  localAccts: any[] = [];
  dropdownLists: { [k: string]: any[] } = { accounts: [] };

  selectedTaxYear = '';
  selectedScenario = '';
  selectedFromAcctKey: any[] = [];

  showScenario = false;
  examScenario = false;

  loadingTaxYears = false;
  loadingScenarios = false;
  loadingAccounts = false;

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Initiate COA fix Process',
    message: 'Are you sure to initiate the COA fix process?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingConfirmAction: (() => void) | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private coaDiagnosticFixService: CoaDiagnosticFixService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();

    document.addEventListener('click', this.onDocClickCapture, true);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    document.removeEventListener('click', this.onDocClickCapture, true);
  }

  private closeMultiSelectIfOutside(event: MouseEvent): void {
    const ms = this.acctMultiSelect;
    if (!ms || !ms.showList) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    // AppKit renders the option list in a CDK overlay attached to <body>, so
    // treat both the field and the overlay panel as "inside".
    const clickedInside =
      !!target.closest('ap-dropdown') ||
      !!target.closest('.ap-dropdown-list');

    if (!clickedInside) {
      ms.hide();
      this.cdr.detectChanges();
    }
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.headerData.workspaceFactory = this.gtwWorkspace.workspaceFactory;
    this.GENERAL_CONFIG = this.headerData.generalConfigData || {};
    this.globalParams = this.headerData.globalParams || {};
    this.userSettings = this.headerData.userSettingsData || {};
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    this.curentyear = this.globalParams.tax_year || '';
    this.scenario = this.globalParams.scenario || '';
    this.jcd_key = this.globalParams.jcd_key || '';
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;

    this.getTaxYearDropDown();
    this.cdr.detectChanges();
  }

  private checkFormLockStatus(): void {
    if (!this.selectedTaxYear || !this.selectedScenario) {
      this.examScenario = false;
      return;
    }

    const lockparams = {
      action_code: 'n10cpv',
      tax_year: this.selectedTaxYear,
      p_consol_group_key: '1',
      p_jcd_key: this.jcd_key,
      p_scenario: this.selectedScenario,
    };

    this.coaDiagnosticFixService
      .checkLockStatus(this.GENERAL_CONFIG.base_url, lockparams, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        const locked = data?.jsonObject;
        this.examScenario = locked?.[0]?.SCENARIO_DESC === 'EXAM';
        if (locked?.[0]?.LOCK_STATUS === 'Y') {
          this.gtwSnackbarService.openSnackBar(
            'Tax Return has already been filed for tax year ' + this.selectedTaxYear + '.',
            'gtw-snackbar--red',
          );
        }
        this.cdr.detectChanges();
      });
  }

  getTaxYearDropDown(): void {
    const params = { action_code: 'e83e1x' };
    const currentTaxYear = parseInt(this.curentyear, 10) || new Date().getFullYear();

    this.loadingTaxYears = true;
    this.cdr.detectChanges();

    this.coaDiagnosticFixService
      .getTaxYearDropDown(this.GENERAL_CONFIG.base_url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          const taxYearData = _.filter(data?.jsonObject, (p: any) =>
            _.includes(['TAX_YEAR'], p.CODE_GROUP),
          );
          this.filteredTaxYearData = [];
          for (const item of taxYearData) {
            const itemYear = parseInt(item.CODE_DISP_DESC, 10);
            if (item.CODE_GROUP === 'TAX_YEAR' && itemYear <= currentTaxYear && itemYear >= currentTaxYear - 5) {
              this.filteredTaxYearData.push(item);
            }
          }
          this.filteredTaxYearData = _.orderBy(this.filteredTaxYearData, ['CODE_DISP_DESC'], ['desc']);
          this.loadingTaxYears = false;
          this.cdr.detectChanges();
        },
        () => {
          this.filteredTaxYearData = [];
          this.loadingTaxYears = false;
          this.cdr.detectChanges();
        },
      );
  }

  onTaxYearChange(): void {
    this.scenarioDropDown = [];
    this.setLocalAccts([]);
    this.selectedScenario = '';
    this.selectedFromAcctKey = [];
    this.showScenario = false;
    this.examScenario = false;
    this.loadingScenarios = false;
    this.loadingAccounts = false;
    this.cdr.detectChanges();
    this.getScenarioDropDown();
  }

  getScenarioDropDown(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar(
        'Please select the Tax Year to get the Scenario.',
        'gtw-snackbar--red',
      );
      return;
    }

    const params = { action_code: '7q0wiy', tax_year: this.selectedTaxYear };

    this.loadingScenarios = true;
    this.cdr.detectChanges();

    this.coaDiagnosticFixService
      .getScenarioDropDown(this.GENERAL_CONFIG.base_url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.scenarioDropDown = data?.jsonObject || [];
          this.showScenario = true;
          this.loadingScenarios = false;
          this.cdr.detectChanges();
        },
        () => {
          this.scenarioDropDown = [];
          this.showScenario = false;
          this.loadingScenarios = false;
          this.cdr.detectChanges();
        },
      );
  }

  onScenarioChange(): void {
    this.setLocalAccts([]);
    this.selectedFromAcctKey = [];
    this.loadingAccounts = false;
    this.cdr.detectChanges();

    if (this.selectedScenario) {
      this.checkFormLockStatus();
      this.getCOAData();
    } else {
      this.examScenario = false;
    }
  }

  getCOAData(): void {
    const params = {
      action_code: 'md63zh',
      scenario: this.selectedScenario,
      tax_year: this.selectedTaxYear,
    };

    this.loadingAccounts = true;
    this.cdr.detectChanges();

    this.coaDiagnosticFixService
      .getCOAData(this.GENERAL_CONFIG.base_url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.setLocalAccts(data?.jsonObject || []);
          this.loadingAccounts = false;
          this.cdr.detectChanges();
        },
        () => {
          this.setLocalAccts([]);
          this.loadingAccounts = false;
          this.cdr.detectChanges();
        },
      );
  }

  confirmFixCoaMapping(): void {
    this.confirmAction(
      'Initiate COA fix Process',
      'Are you sure to initiate the COA fix process?',
      () => this.autoDiagProcess(),
    );
  }

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    if (this.pendingConfirmAction) {
      this.pendingConfirmAction();
      this.pendingConfirmAction = null;
    }
    this.cdr.detectChanges();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }

  autoDiagProcess(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select a valid Tax Year.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedScenario) {
      this.gtwSnackbarService.openSnackBar('Please select a valid Scenario.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedFromAcctKey || this.selectedFromAcctKey.length === 0) {
      this.gtwSnackbarService.openSnackBar('Please Enter Local Account key.', 'gtw-snackbar--red');
      return;
    }

    const acctKeys = this.selectedFromAcctKey.map(info => info.ACCT_KEY).join(',');

    const lockparams = {
      action_code: 'n10cpv',
      tax_year: this.selectedTaxYear,
      p_consol_group_key: '1',
      p_jcd_key: this.jcd_key,
      p_scenario: this.selectedScenario,
    };

    this.coaDiagnosticFixService
      .checkLockStatus(this.GENERAL_CONFIG.base_url, lockparams, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          const locked = data?.jsonObject;
          if (locked?.[0]?.LOCK_STATUS === 'N' && locked?.[0]?.SCENARIO_DESC !== 'EXAM') {
            const issueParams = this.gtwGlobal.isIssueIDEnabled(
              this.selectedTaxYear,
              this.selectedScenario,
            );
            const params: any = {
              sso_id: this.userSettings?.user?.sso_id || this.globalParams?.sso_id,
              action_code: 'pnqj5w',
              tax_year: this.selectedTaxYear,
              scenario: this.selectedScenario,
              p_from_acct_key: acctKeys,
              jcd_key: this.jcd_key,
              issue_id: this.globalParams.issue_id,
              issue_short_desc: this.globalParams.issue_short_desc,
              scenarioCode: issueParams.scenarioCode,
              is_issue_key_enabled: issueParams.is_issue_key_enabled,
            };

            this.coaDiagnosticFixService
              .fixCoaMapping(this.GENERAL_CONFIG.base_url, params, this.headerData).pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(
                (result: any) => {
                  if (result?.callSuccess != 1 && result?.errorMessage) {
                    this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
                    return;
                  }
                  this.cdr.detectChanges();
                },
                () => {
                  this.gtwSnackbarService.openSnackBar(
                    'Error occurred while fixing COA mapping.',
                    'gtw-snackbar--red',
                  );
                  this.cdr.detectChanges();
                },
              );

            this.selectedFromAcctKey = [];
            this.gtwSnackbarService.openSnackBar(
              'Process to Fix a Acct Diag is initiated. Email will be sent, after the process is complete.',
              'gtw-snackbar--green',
            );
            this.cdr.detectChanges();
          } else {
            this.gtwSnackbarService.openSnackBar(
              'Cannot proceed with Process to Fix a Acct Diag for selected Tax Year and Scenario.',
              'gtw-snackbar--red',
            );
            this.cdr.detectChanges();
          }
        },
        () => {
          this.gtwSnackbarService.openSnackBar('Error checking lock status.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      );
  }

  reset(): void {
    this.selectedTaxYear = '';
    this.selectedScenario = '';
    this.selectedFromAcctKey = [];
    this.scenarioDropDown = [];
    this.setLocalAccts([]);
    this.showScenario = false;
    this.examScenario = false;
    this.loadingScenarios = false;
    this.loadingAccounts = false;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  onDropdownFilterResult(key: string, event: any): void {
    this.dropdownLists[key] = event?.result || [];
  }

  private setLocalAccts(items: any[]): void {
    this.localAccts = items || [];
    this.syncDropdownList('accounts', this.localAccts);
  }

  private toAppkitOptions(items: any[]): any[] {
    return (items || []).map((item: any) => {
      item.showCheckbox = true;
      item.role = 'checkbox';
      return item;
    });
  }

  private syncDropdownList(key: string, master: any[]): void {
    this.dropdownLists[key] = this.toAppkitOptions(master).slice();
  }
}
